import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { getSharedObserver, type Observer } from '../observer/Observer';
import { findAncestorAcrossShadow } from '../util/shadowDom';
import type { SatApp } from '../App';

/**
 * Phase 7 chunk 1C — topbar toggle that flips between globe view and
 * sky view (stargazer pose at the observer's location).
 *
 * Disabled with explanatory tooltip when no observer is set (sky view
 * has no anchor) or while in the conjunction-replay route (the replay
 * scene owns the camera per docs/phase7.md § II decision 10).
 *
 * Click → walks to <sat-app> via the shadow-DOM-aware ancestor helper
 * and calls `requestViewMode(target)`. The app handles the Globe
 * entry/exit + state flip + emits `view-mode-changed` so this element
 * (and any other view-aware UI) can re-render.
 */
@customElement('sat-view-toggle')
export class SatViewToggle extends LitElement {
  @state() private observer: Observer | null = null;
  @state() private viewMode: 'globe' | 'sky' = 'globe';
  @state() private inReplayRoute = false;

  private unsubscribeObserver: (() => void) | null = null;

  private viewModeListener = (e: Event): void => {
    const detail = (e as CustomEvent<{ viewMode: 'globe' | 'sky' }>).detail;
    if (detail && (detail.viewMode === 'globe' || detail.viewMode === 'sky')) {
      this.viewMode = detail.viewMode;
    }
  };

  connectedCallback(): void {
    super.connectedCallback();
    const obs = getSharedObserver();
    this.observer = obs.getCurrent();
    this.unsubscribeObserver = obs.subscribe((next) => {
      this.observer = next;
    });
    window.addEventListener('view-mode-changed', this.viewModeListener);
    this.inReplayRoute = window.location.pathname.startsWith('/conjunction/');
    // Pick up the current mode from the host app in case we mount
    // after a URL-restored ?view=sky activation has already happened.
    const app = findAncestorAcrossShadow(this, 'sat-app') as SatApp | null;
    if (app !== null) {
      this.viewMode = app.getViewMode();
    }
  }

  disconnectedCallback(): void {
    this.unsubscribeObserver?.();
    this.unsubscribeObserver = null;
    window.removeEventListener('view-mode-changed', this.viewModeListener);
    super.disconnectedCallback();
  }

  static styles = css`
    :host {
      position: relative;
      display: inline-block;
      font-family: var(--font-mono);
    }
    button {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      padding: 0.3rem 0.6rem;
      background: transparent;
      color: var(--color-text);
      border: 1px solid var(--color-border);
      border-radius: 4px;
      font-family: var(--font-mono);
      font-size: 0.85rem;
      cursor: pointer;
      transition: color 0.1s ease, border-color 0.1s ease;
    }
    button:hover:not([disabled]) {
      border-color: var(--color-accent);
      color: var(--color-accent);
    }
    button[disabled] {
      opacity: 0.5;
      cursor: not-allowed;
    }
    button[data-mode='sky'] {
      color: var(--color-accent);
      border-color: currentColor;
    }
    .toggle__glyph { font-size: 0.95rem; line-height: 1; }

    /* Collapse the label below 1300px to match the other action pills. */
    @media (max-width: 1300px) {
      .toggle__label { display: none; }
    }
  `;

  private handleClick(): void {
    if (this.inReplayRoute) return;
    if (this.observer === null) return;
    const app = findAncestorAcrossShadow(this, 'sat-app') as SatApp | null;
    if (app === null) return;
    const target = this.viewMode === 'sky' ? 'globe' : 'sky';
    app.requestViewMode(target);
  }

  render() {
    const disabled = this.inReplayRoute || this.observer === null;
    const tooltip = this.inReplayRoute
      ? 'Sky view unavailable in replay mode'
      : this.observer === null
        ? 'Set your location first'
        : this.viewMode === 'sky'
          ? 'Return to globe view'
          : 'Look up from your observer location';
    const glyph = this.viewMode === 'sky' ? '↓' : '↑';
    const label = this.viewMode === 'sky' ? 'Globe view' : 'Sky view';
    return html`
      <button
        type="button"
        @click=${this.handleClick}
        ?disabled=${disabled}
        data-mode=${this.viewMode}
        title=${tooltip}
        aria-pressed=${this.viewMode === 'sky' ? 'true' : 'false'}
      >
        <span class="toggle__glyph" aria-hidden="true">${glyph}</span>
        <span class="toggle__label">${label}</span>
      </button>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sat-view-toggle': SatViewToggle;
  }
}
